/**
 * @module composables/useAppMenu
 *
 * Shared application-level state composable. Provides reactive singleton
 * references for global UI state that must be consistent across multiple
 * unrelated components:
 *
 * - Navigation drawer visibility and rail mode (desktop)
 * - Add URL dialog open/close state
 * - Global snackbar notifications (color, message, visible)
 * - Badge counts for Podcasts, Latest, and Playlist nav items
 * - Destination podcasts list for the "Move Episodes" dropdown
 * - View mode preferences (list vs cards/grid) persisted in localStorage
 * - Dynamic AppBar integration: per-view title, thumbnail, action buttons,
 *   context-menu callback, and search-active indicator
 *
 * All refs are declared at module scope (outside the composable function)
 * so they act as singletons — every component that calls useAppMenu()
 * shares the exact same reactive references with no extra event bus needed.
 */

import { ref, watch } from "vue";
// Per seguire l'esito del job di estrazione dopo un "Aggiungi URL".
import { api } from "@/api/config.js";

/** Whether the "all episodes" mode is active (cross-podcast view) */
const isAll = ref(false);

/** Total page count for pagination display */
const pageCount = ref(null);

/** Badge counts for the navigation menu items */
const podcastsCount = ref(null);
const latestCount = ref(null);
const playlistCount = ref(null);

/** Desktop nav drawer visibility (always true on desktop, toggled on mobile) */
const navDrawer = ref(true);

/** Rail mode: when true, drawer shows only icons (default: collapsed) */
const navRail = ref(true);

/** Controls visibility of the Add URL dialog */
const addUrlDialog = ref(false);

/**
 * List of podcasts available as move/copy destinations in dropdowns.
 * Populated by refreshDestinationPodcasts() in feeds.js API module.
 * Only includes "default" type podcasts (user-created, not auto-generated).
 */
const destinationPodcasts = ref([]);

/*
 * Mobile app bar integration refs.
 * Each list view registers these on mount and clears them on unmount,
 * allowing the shared AppBar component to delegate actions to the
 * currently active view.
 */

/** Callback function that opens the current view's context menu (dots menu) */
const viewMenuCallback = ref(null);

/** Callback for a view-specific action button shown in the app bar (left of dots menu) */
const viewActionCallback = ref(null);

/** Icon for the view-specific action button (e.g. "mdi-account-plus") */
const viewActionIcon = ref(null);

/** Callback for a second view-specific action button (e.g. filter) */
const viewAction2Callback = ref(null);

/** Icon for the second view-specific action button (e.g. "mdi-filter") */
const viewAction2Icon = ref(null);

/** Color for the second action button (e.g. orange when filter is active) */
const viewAction2Color = ref(null);

/** When true, the Add URL (link) button is hidden from the app bar */
const hideAddUrl = ref(false);

/** When true, the dots menu icon turns orange to indicate an active search filter */
const viewMenuSearchActive = ref(false);

/**
 * Callback that clears the current view's active search/filter. When set AND a
 * filter is active (viewMenuSearchActive), the app bar shows a dedicated
 * "clear filter" funnel icon so the user can cancel the filter directly,
 * instead of having to dig back into the dots menu. Each searchable view
 * registers its own reset function here on mount and clears it on unmount.
 */
const viewClearFilterCallback = ref(null);

/**
 * Getter function returning a one-line description of the current view's
 * ACTIVE search/filter (e.g. `Filtered: “star” · Starred`). Shown as the
 * tooltip on the app-bar clear-filter funnel so the user is reminded what
 * they searched for before clearing it. Registered as a FUNCTION (not a
 * string) so the tooltip stays live-reactive: AppBar calls it inside a
 * computed, which tracks whatever refs the getter reads (query, status, …).
 * Each searchable view registers its getter on mount and clears it on unmount.
 */
const viewFilterDescription = ref(null);

/** Overrides the default route-based title in the app bar (e.g., podcast name) */
const viewTitle = ref(null);

/** Shows a podcast avatar/thumbnail in the app bar instead of the generic page icon */
const viewThumbnail = ref(null);

/** Global snackbar notification state shared across all components */
const snackbar = ref({
  show: false,
  message: null,
  color: "info",
});

/**
 * Caching progress state for cache-with-progress operations.
 * Used by list view polling to update progress during caching.
 */

/** ID of episode currently being cached (null if none) */
const cachingEpisodeId = ref(null);

/** Download progress percentage (0-100) during cache operation */
const cachingProgress = ref(0);

/** Status message for caching (e.g., "Caching episode... 45%") */
const cachingStatusMessage = ref("");

/** Title of the episode currently being cached, for snackbar display */
const cachingEpisodeTitle = ref("");

/**
 * Raw server-side progress_message for the episode currently being cached
 * (e.g. "downloading", "Uploading to cloud..."). Surfaced so the player tab's
 * "preparing" overlay can label WHICH phase is happening — downloading the
 * media from its source vs. uploading the finished file to R2 cloud storage —
 * instead of a generic "downloading" for the whole wait.
 */
const cachingProgressMessage = ref("");

/**
 * Error message when the episode the player tab is preparing FAILED to download
 * (e.g. "unable to download video data: HTTP Error 403: Forbidden"). Set, the
 * preparing overlay shows an error state with a Close button instead of an
 * endless spinner. Empty when there's no error.
 */
const cachingError = ref("");

/**
 * Count of episodes currently being downloaded/uploaded, across ALL of the
 * user's podcasts + Latest + Playlist (an episode reachable through more
 * than one of those still counts once). The AppBar shows a persistent
 * indicator whenever this is > 0, regardless of which page is open.
 *
 * Sole writer: App.vue's global poll of GET /episodes/downloading-count,
 * every 15s, independent of route (started on login, stopped + zeroed on
 * logout). Used to also be written by the three list views' own 5s status
 * polls (ListLatestView/ListEpisodesView/PlaylistView) as a side effect —
 * removed because each of those computed a PARTIAL count (just that one
 * already-loaded list) and, being on a different timer than the global
 * poll, raced it on this same ref: the badge visibly flickered between a
 * smaller wrong number and the correct total every few seconds while on
 * any of those three pages. It was also never live AT ALL on any OTHER
 * page (Settings, Sideload, Search, ...) before the global poll existed —
 * reported directly: an in-progress sideload upload showed no indicator
 * anywhere, because the page you're actually on right after starting one
 * isn't Latest/Episodes/Playlist.
 */
const downloadingCount = ref(0);

/**
 * Status-page queue activity, surfaced as progress indicators in the AppBar.
 * Set by StatusView from its polling; null when there's no active queue work.
 * Shape: { video, videoProgress, metadata, monitor } where each count is the
 * number of active jobs in that queue (0 hides that indicator).
 */
const statusActivity = ref(null);

/**
 * Global "busy" flag for page-level loading (fetching episodes, podcasts,
 * playlist, latest, etc.). Drives a single non-blocking linear progress bar
 * at the bottom of the AppBar — replacing the old blocking v-overlay spinners
 * that, if left stuck, froze the page (no scroll/interaction). Views sync their
 * local loading ref to this and clear it on unmount.
 *
 * (The per-episode download circular indicator — with percentage — is separate
 * and intentionally NOT driven by this.)
 */
const busy = ref(false);

/**
 * View mode preference for the podcasts list.
 * Persisted in localStorage so it survives page reloads.
 * When true, podcasts are shown as cards; when false, as a list.
 * Defaults to false (list view) if not set or if localStorage is unavailable.
 */
const podcastsAsListOfCards = ref((function() {
  try {
    const stored = localStorage.getItem("podcastsAsListOfCards");
    return stored !== null ? JSON.parse(stored) : false;
  } catch (err) {
    console.error("Error parsing podcastsAsListOfCards from localStorage:", err);
    return false;
  }
})());

/**
 * View mode preference for the episodes list.
 * Persisted in localStorage so it survives page reloads.
 * Defaults to false (list view) if not set or if localStorage is unavailable.
 */
const episodesAsListOfCards = ref((function() {
  try {
    const stored = localStorage.getItem("episodesAsListOfCards");
    return stored !== null ? JSON.parse(stored) : false;
  } catch (err) {
    console.error("Error parsing episodesAsListOfCards from localStorage:", err);
    return false;
  }
})());

/**
 * View mode preference for the playlist.
 * Persisted in localStorage so it survives page reloads.
 * Defaults to false (list view) if not set or if localStorage is unavailable.
 */
const playlistAsGrid = ref((function() {
  try {
    const stored = localStorage.getItem("playlistAsGrid");
    return stored !== null ? JSON.parse(stored) : false;
  } catch (err) {
    console.error("Error parsing playlistAsGrid from localStorage:", err);
    return false;
  }
})());

/**
 * Global display preference: show the AI-generated episode summary below
 * the title/subtitle/tags block on grid/card view episode tiles (Latest,
 * Episodes, Playlist — every view with a grid mode). Set from the Settings
 * page ("Show AI summary in grid mode"). Defaults to OFF — a summary can
 * run to several sentences plus bullet points, and showing it on every
 * card by default would make an already-dense grid much taller; this is
 * opt-in the same way the tag-inheritance/podcast-label features are.
 * Persisted in localStorage via the watcher below (mirrors
 * podcastsAsListOfCards/episodesAsListOfCards/playlistAsGrid above, except
 * those persist at each individual toggle call site — this one persists
 * itself here since it currently has exactly one call site, Settings).
 */
const showAiSummaryInGrid = ref((function() {
  try {
    const stored = localStorage.getItem("showAiSummaryInGrid");
    return stored !== null ? JSON.parse(stored) : false;
  } catch (err) {
    console.error("Error parsing showAiSummaryInGrid from localStorage:", err);
    return false;
  }
})());
watch(showAiSummaryInGrid, (value) => {
  try {
    localStorage.setItem("showAiSummaryInGrid", JSON.stringify(value));
  } catch (err) {
    console.error("Error persisting showAiSummaryInGrid to localStorage:", err);
  }
});

/**
 * useAppMenu - Returns the shared application-level state object.
 *
 * Because all refs are module-scoped singletons, every component that
 * calls this function gets the same reactive references — mutations in
 * one component are immediately visible in all others without lifting
 * state or passing props.
 *
 * Typical usage:
 *   const { snackbar, addUrlDialog, podcastsCount } = useAppMenu();
 *
 * @returns {Object} All shared app menu state refs
 */
export function useAppMenu() {

/**
 * seguiJobMetadata - Riporta all'utente l'esito di un "Aggiungi URL".
 *
 * POST /add-url accoda e basta: risponde 200 in una manciata di millisecondi,
 * molto prima che l'estrazione sia iniziata. Quando questa fallisce — un 403 di
 * Rumble, un video privato, un sito che ha cambiato struttura — la risposta HTTP
 * è partita da un pezzo e non c'è più nessuno a cui dirlo. Il fallimento finiva
 * solo nella sezione "Failed" della pagina Status, che nessuno ha motivo di
 * aprire dopo aver incollato un link: dal punto di vista di chi lo ha incollato,
 * l'URL semplicemente non compariva mai, senza spiegazione.
 *
 * Qui si segue quel job finché non si sa com'è andata, e si parla solo se è
 * andata male: un successo si vede già da sé, perché l'episodio compare in
 * Latest.
 *
 * Vive nel composable e non nel componente perché il dialogo si chiude subito
 * dopo l'invio — un intervallo dentro un componente smontato non
 * sopravviverebbe.
 *
 * @param {string} jobId - id restituito da POST /add-url
 * @param {string} url - mostrato nel messaggio, per capire QUALE link ha fallito
 */
const seguiJobMetadata = async (jobId, url) => {
  if (!jobId) return;
  const OGNI_MS = 4000;
  const TENTATIVI = 45; // ~3 minuti: l'estrazione dura secondi, non minuti

  const breve = url && url.length > 60 ? url.slice(0, 60) + "…" : url;

  for (let i = 0; i < TENTATIVI; i++) {
    await new Promise((r) => setTimeout(r, OGNI_MS));
    let coda;
    try {
      const risposta = await api.get("/queue/status/metadata");
      coda = risposta.data;
    } catch {
      return; // Rete assente o sessione scaduta: non è il momento di insistere.
    }
    // Nel job store la chiave è `id`, non `jobId`.
    const fallito = (coda.failed || []).find((j) => j.id === jobId);
    if (fallito) {
      const err = fallito.error;
      const motivo =
        (typeof err === "string" && err) || err?.message || "estrazione non riuscita";
      snackbar.value = {
        show: true,
        color: "error",
        message: `Non sono riuscito ad aggiungere ${breve}: ${motivo}`,
      };
      return;
    }
    // Riuscito: l'episodio è già comparso in Latest, non serve dirlo.
    if ((coda.completed || []).some((j) => j.id === jobId)) return;
  }
  // Scaduto il tempo senza un esito: tacere è meglio che allarmare per un job
  // che magari sta ancora lavorando.
};

  return {
    destinationPodcasts,
    isAll,
    snackbar,
    addUrlDialog,
    seguiJobMetadata,
    navDrawer,
    navRail,
    episodesAsListOfCards,
    podcastsAsListOfCards,
    playlistAsGrid,
    showAiSummaryInGrid,
    pageCount,
    podcastsCount,
    latestCount,
    playlistCount,
    viewMenuCallback,
    viewMenuSearchActive,
    viewClearFilterCallback,
    viewFilterDescription,
    viewActionCallback,
    viewActionIcon,
    viewAction2Callback,
    viewAction2Icon,
    viewAction2Color,
    hideAddUrl,
    viewTitle,
    viewThumbnail,
    cachingEpisodeId,
    cachingProgress,
    cachingStatusMessage,
    cachingEpisodeTitle,
    cachingProgressMessage,
    cachingError,
    downloadingCount,
    busy,
    statusActivity,
  };
}
